import * as Yup from 'yup';

import { validateField, ValidationType } from './formValidation';
import {
    ApplicantFormInterface,
    OrganizationAdditionalDetailsFormInterface,
    OrganizationOnboardingFormInterface,
    OrganizationIdentificationFormInterface,
    OrganizationInterface,
    SalesOnboardingFormInterface,
} from 'types';
import api from 'api';

export const salesOnboardingInitialValues: SalesOnboardingFormInterface = {
    firstName: '',
    lastName: '',
    email: '',
    selfAssessment: false,
    showResults: false,
};

/**
 * @param takenEmail - normalized email known to be already registered.
 * Kept sync so other fields can re-validate without calling the API.
 */
export const salesOnboardingFormValidationSchema = (
    translate: (value: string) => string,
    takenEmail?: string | null
) =>
    Yup.object({
        firstName: validateField(ValidationType.STRING),
        lastName: validateField(ValidationType.STRING),
        email: validateField(ValidationType.EMAIL).test(
            'email-exists',
            translate('emailAlreadyInUse'),
            (value) => !value || !takenEmail || value.trim().toLowerCase() !== takenEmail
        ),
    });

export const applicantFormInitialValues: ApplicantFormInterface = {
    firstName: '',
    lastName: '',
    email: '',
    selfAssessment: false,
    showResults: false,
    service_provider_id: null,
};

export const applicantFormValidationSchema = (translate: (value: string) => string) =>
    Yup.object({
        firstName: validateField(ValidationType.STRING),
        lastName: validateField(ValidationType.STRING),
        email: validateField(ValidationType.EMAIL).test(
            'email-exists',
            translate('emailAlreadyInUse'),
            async (value) => {
                if (!value) return true;

                try {
                    await api.checkEmail.get(value);
                    return true;
                } catch {
                    return false;
                }
            }
        ),
        service_provider_id: validateField(ValidationType.NUMBER),
    });

export const organizationOnboardingFormInitialValues: OrganizationOnboardingFormInterface = {
    company_name: '',
    firstname: '',
    lastname: '',
    contact_email: '',
    onboardingType: 'manualIntake',
};

export const organizationFormValidationSchema = (translate: (value: string) => string) =>
    Yup.object({
        company_name: validateField(ValidationType.STRING),
        firstname: validateField(ValidationType.STRING),
        lastname: validateField(ValidationType.STRING),
        contact_email: validateField(ValidationType.EMAIL).test(
            'email-exists',
            translate('emailAlreadyInUse'),
            async (value) => {
                if (!value) return true;

                try {
                    await api.checkEmail.get(value);
                    return true;
                } catch {
                    return false;
                }
            }
        ),
    });

// organization profile form

export const organizationInitialValues: OrganizationInterface = {
    email: '',
    company_name: '',
    legal_entity_name: '',

    country: '',
    region: '',
    city: '',
    address: '',
    zip_code: '',
    phone_number: '',
    vat_number: '',
    organization_type: '',
    date_established: '',
    industry: '',
    products_description: '',
    operates_from_physical_location: null,
    number_employees: null,
    number_canadian_employees: null,
    annual_revenue: null,
    applied_for_lmia_prior: null,
    has_been_banned_from_hiring_in_canada: null,
};

export const organizationValidationSchema = Yup.object({
    email: validateField(ValidationType.STRING, false),
    company_name: validateField(ValidationType.STRING),
    legal_entity_name: validateField(ValidationType.STRING),

    country: validateField(ValidationType.STRING, false),
    region: validateField(ValidationType.STRING),
    city: validateField(ValidationType.STRING, false),
    address: validateField(ValidationType.STRING),
    zip_code: validateField(ValidationType.STRING, false),
    // phone_number: validateField(ValidationType.PHONE, false),
    vat_number: validateField(ValidationType.STRING),
    organization_type: validateField(ValidationType.STRING),
    date_established: validateField(ValidationType.DATE),
    industry: validateField(ValidationType.STRING),
    products_description: validateField(ValidationType.STRING),
    operates_from_physical_location: validateField(ValidationType.BOOLEAN_VALUE),
    number_employees: validateField(ValidationType.NUMBER),
    number_canadian_employees: validateField(ValidationType.NUMBER),
    annual_revenue: validateField(ValidationType.NUMBER),
    applied_for_lmia_prior: validateField(ValidationType.BOOLEAN_VALUE),
    has_been_banned_from_hiring_in_canada: validateField(ValidationType.BOOLEAN_VALUE),
});

// delete below forms

export const organizationIdentificationInitialValues: OrganizationIdentificationFormInterface = {
    legalBusinessName: '',
    organizationType: '',
    contactPersonName: '',
    contactPersonPhone: '',
    taxNumber: '',
    businessAddress: '',
    province: '',
    yearEstablished: '',
};

export const organizationIdentificationValidationSchema = Yup.object({
    legalBusinessName: validateField(ValidationType.STRING),
    organizationType: validateField(ValidationType.STRING),
    contactPersonName: validateField(ValidationType.STRING),
    contactPersonPhone: validateField(ValidationType.PHONE),
    taxNumber: validateField(ValidationType.STRING),
    businessAddress: validateField(ValidationType.STRING),
    province: validateField(ValidationType.STRING),
    yearEstablished: validateField(ValidationType.INTEGER_POSITIVE, true, 'Enter a valid year'),
});

export const organizationAdditionalDetailsInitialValues: OrganizationAdditionalDetailsFormInterface =
    {
        industry: '',
        businessDescription: '',
        physicalCommercialLocation: '',
        totalEmployees: '',
        canadianEmployees: '',
        annualRevenue: '',
        appliedLMIAInPast: '',
        lmiaWasSuccessful: '',
        lmiaAppliedWhen: '',
        everBannedOrRestricted: '',
    };

export const organizationAdditionalDetailsValidationSchema = Yup.object({
    industry: validateField(ValidationType.STRING, false),
    businessDescription: validateField(ValidationType.STRING, false),
    physicalCommercialLocation: validateField(ValidationType.STRING),
    totalEmployees: validateField(ValidationType.INTEGER, true, 'Enter a valid number'),
    canadianEmployees: validateField(ValidationType.INTEGER, true, 'Enter a valid number').test(
        'canadian-employees-lte-total',
        'Cannot exceed total employees',
        function (value) {
            const total = this.parent?.totalEmployees;
            if (value == null || total == null || value === '' || total === '') return true;
            return Number(value) <= Number(total);
        }
    ),
    annualRevenue: validateField(ValidationType.STRING, true, 'This field is required'),
    appliedLMIAInPast: validateField(ValidationType.STRING),
    lmiaWasSuccessful: Yup.string()
        .nullable()
        .when('appliedLMIAInPast', (appliedLMIAInPast, schema) => {
            const value = Array.isArray(appliedLMIAInPast)
                ? appliedLMIAInPast[0]
                : appliedLMIAInPast;
            return value === 'Yes' ? schema.required('This field is required') : schema;
        }),
    lmiaAppliedWhen: Yup.string()
        .nullable()
        .when('appliedLMIAInPast', (appliedLMIAInPast, schema) => {
            const value = Array.isArray(appliedLMIAInPast)
                ? appliedLMIAInPast[0]
                : appliedLMIAInPast;
            return value === 'Yes' ? schema.required('This field is required') : schema;
        }),
    everBannedOrRestricted: validateField(ValidationType.STRING),
});
